const Row = ({ label, value }) => (
  <div className="py-1.5">
    <span className="text-xs text-base-content/50 block">{label}</span>
    <span className="text-sm whitespace-pre-wrap">{value || '—'}</span>
  </div>
);

/** Couleurs par défaut du formulaire (alignées sur le portail / la page Design) */
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_PRIMARY = '#007AFF';
const DEFAULT_SECONDARY = '#FF2D55';

/**
 * Pastille de couleur + code hexadécimal (lecture seule).
 * @param {Object} props - { label, value, fallback }
 * @returns {JSX.Element} Bloc pastille + hex
 */
const ColorSwatch = ({ label, value, fallback }) => {
  const isValid = HEX_COLOR_REGEX.test(String(value || ''));
  const color = isValid ? value : fallback;
  return (
    <div className="flex items-center gap-2">
      <span
        className="inline-block w-8 h-8 rounded-lg border border-base-300 flex-shrink-0"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <div>
        <span className="text-xs text-base-content/50 block">{label}</span>
        <span className="text-sm font-mono">
          {isValid ? color.toUpperCase() : `${fallback} (défaut)`}
        </span>
      </div>
    </div>
  );
};

/**
 * Affichage des informations du formulaire de la demande (entreprise, activité, services).
 */
const RequestInfo = ({ request }) => {
  const personal = request.personalInfo || {};
  const biz = request.businessInfo || {};
  const activity = request.activity || {};
  const services = Array.isArray(request.services) ? request.services : [];
  const attachments = request.attachments || {};
  const design = request.design || {};
  const attachmentImages = Array.isArray(attachments.images) ? attachments.images : [];
  const faq = (Array.isArray(request.faqAnswers) ? request.faqAnswers : [])
    .filter((entry) => entry && String(entry.question || '').trim() && String(entry.answer || '').trim());

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <h4 className="font-semibold text-sm mb-2">Client</h4>
        <Row label="Prénom" value={personal.firstName} />
        <Row label="Nom" value={personal.lastName} />
        <Row label="Email" value={request.userInfo?.email} />
        <Row label="Téléphone" value={biz.telephone} />
      </div>
      <div>
        <h4 className="font-semibold text-sm mb-2">Entreprise</h4>
        <Row label="SIRET" value={biz.siret} />
        <Row label="Nom commercial" value={biz.nomCommercial} />
        <Row
          label="Adresse"
          value={[biz.adresse?.rue, biz.adresse?.codePostal, biz.adresse?.ville].filter(Boolean).join(', ')}
        />
        <Row label="Horaires" value={biz.horaires} />
        <Row label="Accessibilité" value={biz.accessibilite} />
      </div>
      <div>
        <h4 className="font-semibold text-sm mb-2">Activité</h4>
        <Row label="Métier" value={activity.metier} />
        <Row label="Clientèle cible" value={activity.cible} />
        <Row label="Résumé" value={activity.resumeActivite} />

        <h4 className="font-semibold text-sm mt-4 mb-1">Services ({services.length})</h4>
        {services.length === 0 && <p className="text-sm text-base-content/50">Aucun service</p>}
        {services.map((s, index) => (
          <div key={index} className="border border-base-200 rounded-lg p-2 mb-2">
            <p className="text-sm font-medium">
              {s.nom} <span className="text-base-content/60">· {s.prix}</span>
            </p>
            <p className="text-xs text-base-content/60">{s.description}</p>
            {s.lienReservation && <p className="text-xs text-base-content/50">Réservation : {s.lienReservation}</p>}
          </div>
        ))}
      </div>

      {/* Adresse web souhaitée + questions fréquentes du client */}
      <div className="sm:col-span-2">
        <h4 className="font-semibold text-sm mb-2">Adresse web & questions fréquentes</h4>
        <Row label="Adresse web souhaitée" value={request.domainWish} />
        {faq.length === 0 ? (
          <p className="text-sm text-base-content/50">
            Aucune question ajoutée par le client (FAQ rédigée par l'équipe).
          </p>
        ) : (
          <div className="space-y-2 mt-1">
            {faq.map((entry, index) => (
              <div key={index} className="border border-base-200 rounded-lg p-2">
                <p className="text-sm font-medium">Q : {entry.question}</p>
                <p className="text-xs text-base-content/60 whitespace-pre-wrap">R : {entry.answer}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Identité visuelle souhaitée (couleurs de charte) */}
      <div className="sm:col-span-2">
        <h4 className="font-semibold text-sm mb-2">Identité visuelle souhaitée</h4>
        <div className="flex flex-wrap gap-6">
          <ColorSwatch label="Couleur primaire" value={design.primaryColor} fallback={DEFAULT_PRIMARY} />
          <ColorSwatch label="Couleur secondaire" value={design.secondaryColor} fallback={DEFAULT_SECONDARY} />
        </div>
        <p className="text-xs text-base-content/50 mt-2">
          Préférences déclarées par le client à l'étape « Identité & médias » — modifiables par lui
          depuis sa page Design après la mise en ligne.
        </p>
      </div>

      {/* Pièces jointes */}
      <div className="sm:col-span-2">
        <h4 className="font-semibold text-sm mb-2">Pièces jointes</h4>
        {attachmentImages.length === 0 && !attachments.logoJourUrl && !attachments.logoNuitUrl && (
          <p className="text-sm text-base-content/50">Aucune pièce jointe.</p>
        )}
        {attachmentImages.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
            {attachmentImages.map((img, index) => (
              <a key={img.url || index} href={img.url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={img.url}
                  alt={img.name || `Image ${index + 1}`}
                  className="w-full h-20 object-cover rounded-lg border border-base-200"
                />
              </a>
            ))}
          </div>
        )}
        {(attachments.logoJourUrl || attachments.logoNuitUrl) && (
          <div className="flex flex-wrap gap-4">
            {attachments.logoJourUrl && (
              <div>
                <p className="text-xs text-base-content/50 mb-1">Logo (clair)</p>
                <img
                  src={attachments.logoJourUrl}
                  alt="Logo mode clair"
                  className="h-16 border border-base-200 rounded-lg bg-white p-1"
                />
              </div>
            )}
            {attachments.logoNuitUrl && (
              <div>
                <p className="text-xs text-base-content/50 mb-1">Logo (sombre)</p>
                <img
                  src={attachments.logoNuitUrl}
                  alt="Logo mode sombre"
                  className="h-16 border border-base-200 rounded-lg bg-neutral p-1"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestInfo;
